import {
  NotificationService,
  OuiBoxComponent,
  OuiButtonComponent,
  OuiCenterComponent,
  OuiRecordDatasource as OuiDatasource,
  OuiFileDropZoneComponent,
  OuiGridComponent,
  OuiRowComponent,
  OuiSelectComponent,
  OuiStackComponent,
  OuiTextComponent,
} from '@aiz-gl/omni-ui';
import { HttpEventType } from '@angular/common/http';
import {
  Component,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CardComponent } from '../../components/card/card.component';
import { DownloadButtonComponent } from '../../components/download-button/download-button.component';
import { LogTableComponent } from '../../components/log-table/log-table.component';
import { PageComponent } from '../../components/page/page';
import { WorkflowLogEntry } from '../../models/workflow-history.model';
import { SalesChannelService } from '../../services/sales-channel/sales-channel.service';
import { SkuMappingService } from '../../services/sku-mapping/sku-mapping.service';
import { BasePageComponent } from '../base/base.component';

@Component({
  selector: 'app-sku-mapping',
  standalone: true,
  imports: [
    OuiStackComponent,
    OuiCenterComponent,
    OuiRowComponent,
    OuiBoxComponent,
    OuiGridComponent,
    OuiTextComponent,
    OuiButtonComponent,
    OuiFileDropZoneComponent,
    DownloadButtonComponent,
    LogTableComponent,
    PageComponent,
    CardComponent,
    OuiSelectComponent,
  ],
  templateUrl: './sku-mapping.component.html',
})
export class SkuMappingPage extends BasePageComponent {
  protected readonly skuMapping = inject(SkuMappingService);
  protected readonly salesChannel = inject(SalesChannelService);
  private readonly notification = inject(NotificationService);

  permissionToUpload = this.permissionTo('product:manage');

  selectedFile = signal<File | undefined>(undefined);
  downloadSalesChannelCode: string | undefined;
  uploadSalesChannelCode: string | undefined;

  fileDropZone = viewChild<OuiFileDropZoneComponent>('fileDropZone');

  uploadHistoryDatasource?: OuiDatasource<WorkflowLogEntry>;

  constructor() {
    super();

    this.skuMapping.uploadHistory.reload();

    effect(() => {
      const data = this.skuMapping.uploadHistory.value();
      if (!data) return;
      this.uploadHistoryDatasource = new OuiDatasource<WorkflowLogEntry>(data);
    });
  }

  skuMappingFileName = computed(() =>
    [
      'ATB',
      'SKU_Mapping',
      this.downloadSalesChannelCode,
      new Date().toISOString().slice(0, 10),
    ]
      .filter(Boolean)
      .join('_'),
  );

  handleFileAttached(file: File | undefined) {
    if (file) this.selectedFile.set(file);
  }

  uploadFile() {
    const file = this.selectedFile();
    const salesChannelCode = this.uploadSalesChannelCode;
    const validationErrorMessage =
      'The file that you have uploaded has some validation errors. Please upload valid files.';

    if (!file || !salesChannelCode) return;

    this.skuMapping.uploadSkuMappingFile(file, salesChannelCode).subscribe({
      next: (event) => {
        if (event.type !== HttpEventType.Response) return;

        const response = event.body as { is_valid?: boolean } | null;
        if (response?.is_valid === false) {
          this.notification.showAlert({
            message: validationErrorMessage,
            variant: 'warn',
            symbolName: 'error',
          });
          return;
        }

        this.fileDropZone()?.selectedFile.set(undefined);
        this.selectedFile.set(undefined);
      },
      error: (err) => {
        const rawPayload = err?.error;
        let payload: {
          is_valid?: boolean;
          message?: string;
          errors?: unknown[];
        } | null = rawPayload;

        if (typeof rawPayload === 'string') {
          try {
            payload = JSON.parse(rawPayload);
          } catch {
            payload = null;
          }
        }

        if (payload?.is_valid === false) {
          this.notification.showAlert({
            message: validationErrorMessage,
            variant: 'warn',
            symbolName: 'error',
          });
          return;
        }

        const validationErrors = Array.isArray(payload?.errors)
          ? payload.errors.filter((message: unknown): message is string =>
              typeof message === 'string',
            )
          : [];
        const errorMessage = [
          typeof payload === 'string' ? payload : payload?.message,
          ...validationErrors,
        ]
          .filter((message): message is string => Boolean(message?.trim()))
          .join(' ');
        this.notification.showAlert({
          message:
            errorMessage || 'Failed to upload the file. Please try again.',
          variant: 'warn',
          symbolName: 'error',
        });
      },
    });
  }
}
